import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  CurrencyDollarIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  LightBulbIcon,
  DocumentTextIcon,
  ChartPieIcon,
} from '@heroicons/react/24/outline';

const Services = () => {
  const { t } = useTranslation();

  const services = [
    {
      category: t('services.liquidity.title'),
      items: [
        {
          title: t('services.liquidity.exchange'),
          description: t('services.liquidity.exchangeDesc'),
          icon: CurrencyDollarIcon,
        },
        {
          title: t('services.liquidity.token'),
          description: t('services.liquidity.tokenDesc'),
          icon: ChartBarIcon,
        },
        {
          title: t('services.liquidity.defi'),
          description: t('services.liquidity.defiDesc'),
          icon: ShieldCheckIcon,
        },
      ],
    },
    {
      category: t('services.consulting.title'),
      items: [
        {
          title: t('services.consulting.strategy'),
          description: t('services.consulting.strategyDesc'),
          icon: LightBulbIcon,
        },
        {
          title: t('services.consulting.token'),
          description: t('services.consulting.tokenDesc'),
          icon: DocumentTextIcon,
        },
      ],
    },
    {
      category: t('services.risk.title'),
      items: [
        {
          title: t('services.risk.trading'),
          description: t('services.risk.tradingDesc'),
          icon: ShieldCheckIcon,
        },
        {
          title: t('services.risk.market'),
          description: t('services.risk.marketDesc'),
          icon: ChartPieIcon,
        },
      ],
    },
  ];

  return (
    <>
      <Helmet>
        <title>{t('services.meta.title')} - 9527.fi</title>
        <meta name="description" content={t('services.meta.description')} />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-primary text-white py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <motion.h1 
              className="text-4xl font-bold mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {t('services.hero.title')}
            </motion.h1>
            <motion.p 
              className="text-xl text-gray-200"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              {t('services.hero.subtitle')}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          {services.map((category, categoryIndex) => (
            <motion.div
              key={category.category}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: categoryIndex * 0.2 }}
              className="mb-20"
            >
              <h2 className="text-3xl font-bold mb-12 text-center">{category.category}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {category.items.map((service, serviceIndex) => (
                  <motion.div
                    key={service.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: serviceIndex * 0.1 }}
                    className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow"
                  >
                    <service.icon className="h-12 w-12 text-primary mb-4" />
                    <h3 className="text-xl font-semibold mb-2">{service.title}</h3>
                    <p className="text-gray-600">{service.description}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">{t('services.cta.title')}</h2>
          <p className="text-xl text-gray-600 mb-8">
            {t('services.cta.subtitle')}
          </p>
          <motion.a
            href="mailto:stanley@9527.fi"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="btn btn-primary inline-block"
          >
            {t('common.getQuote')}
          </motion.a>
        </div>
      </section>
    </>
  );
};

export default Services; 